import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import './ReportAnalysis.css';

const BalanceSheet = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [balanceData, setBalanceData] = useState({
    currentAssets: {
      debtors: 0,
      stock: 0,
      cash: 0
    },
    fixedAssets: {
      totalValue: 0
    },
    currentLiabilities: {
      creditors: 0,
      loans: 0,
      gstPayable: 0
    },
    longTermLiabilities: {
      loans: 0
    }
  });

  useEffect(() => {
    fetchBalanceSheet();
  }, [selectedDate, selectedMonth, selectedYear]);

  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const db = getDatabase();

      // Fetch Debtors (Accounts Receivable)
      const debtorsRef = ref(db, 'HTAMS/expenses/debtorledger');
      const debtorsSnapshot = await get(debtorsRef);
      let debtorsTotal = 0;
      if (debtorsSnapshot.exists()) {
        const debtorsData = debtorsSnapshot.val();
        debtorsTotal = Object.values(debtorsData).reduce((sum, debtor) => {
          return sum + (parseFloat(debtor.debitAmount) || 0);
        }, 0);
      }

      // Fetch Stock Balance
      const stockRef = ref(db, 'HTAMS/expenses/stockbalance');
      const stockSnapshot = await get(stockRef);
      let stockTotal = 0;
      if (stockSnapshot.exists()) {
        const stockData = stockSnapshot.val();
        stockTotal = Object.values(stockData).reduce((sum, stock) => {
          return sum + (parseFloat(stock.currentValue) || 0);
        }, 0);
      }

      // Fetch Payment Entry for Cash calculation
      const paymentsRef = ref(db, 'HTAMS/expenses/paymententry');
      const paymentsSnapshot = await get(paymentsRef);
      let paymentsTotal = 0;
      if (paymentsSnapshot.exists()) {
        const paymentsData = paymentsSnapshot.val();
        paymentsTotal = Object.values(paymentsData).reduce((sum, payment) => {
          return sum + (parseFloat(payment.amount) || 0);
        }, 0);
      }

      // Fetch Purchase Entry for Cash calculation
      const purchasesRef = ref(db, 'HTAMS/expenses/purchaseentry');
      const purchasesSnapshot = await get(purchasesRef);
      let purchasesTotal = 0;
      if (purchasesSnapshot.exists()) {
        const purchasesData = purchasesSnapshot.val();
        purchasesTotal = Object.values(purchasesData).reduce((sum, purchase) => {
          return sum + (parseFloat(purchase.totalAmount) || 0);
        }, 0);
      }

      // Calculate Cash (this is simplified - you might want a separate cash ledger)
      const cashBalance = Math.max(0, debtorsTotal - paymentsTotal);

      // Fetch Fixed Assets
      const assetsRef = ref(db, 'HTAMS/expenses/assetledger');
      const assetsSnapshot = await get(assetsRef);
      let fixedAssetsTotal = 0;
      if (assetsSnapshot.exists()) {
        const assetsData = assetsSnapshot.val();
        fixedAssetsTotal = Object.values(assetsData).reduce((sum, asset) => {
          return sum + (parseFloat(asset.purchaseAmount) || 0);
        }, 0);
      }

      // Fetch Creditors (Accounts Payable)
      const creditorsRef = ref(db, 'HTAMS/expenses/creditorledger');
      const creditorsSnapshot = await get(creditorsRef);
      let creditorsTotal = 0;
      if (creditorsSnapshot.exists()) {
        const creditorsData = creditorsSnapshot.val();
        creditorsTotal = Object.values(creditorsData).reduce((sum, creditor) => {
          return sum + (parseFloat(creditor.creditAmount) || 0);
        }, 0);
      }

      // Fetch Loans
      const loansRef = ref(db, 'HTAMS/expenses/loanaccountledger');
      const loansSnapshot = await get(loansRef);
      let shortTermLoans = 0;
      let longTermLoans = 0;
      if (loansSnapshot.exists()) {
        const loansData = loansSnapshot.val();
        Object.values(loansData).forEach(loan => {
          const closingBalance = parseFloat(loan.closingBalance) || 0;
          const assetLife = parseFloat(loan.assetLife) || 0;
          // Consider loans < 1 year as short-term
          if (assetLife < 1) {
            shortTermLoans += closingBalance;
          } else {
            longTermLoans += closingBalance;
          }
        });
      }

      // Fetch GST Payable
      const gstRef = ref(db, 'HTAMS/expenses/gstpayable');
      const gstSnapshot = await get(gstRef);
      let gstTotal = 0;
      if (gstSnapshot.exists()) {
        const gstData = gstSnapshot.val();
        gstTotal = Object.values(gstData).reduce((sum, gst) => {
          return sum + (parseFloat(gst.payableAmount) || 0);
        }, 0);
      }

      setBalanceData({
        currentAssets: {
          debtors: debtorsTotal,
          stock: stockTotal,
          cash: cashBalance
        },
        fixedAssets: {
          totalValue: fixedAssetsTotal
        },
        currentLiabilities: {
          creditors: creditorsTotal,
          loans: shortTermLoans,
          gstPayable: gstTotal
        },
        longTermLiabilities: {
          loans: longTermLoans
        }
      });
    } catch (error) {
      console.error('Error fetching balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalCurrentAssets = balanceData.currentAssets.debtors + 
                             balanceData.currentAssets.stock + 
                             balanceData.currentAssets.cash;
  const totalFixedAssets = balanceData.fixedAssets.totalValue;
  const totalAssets = totalCurrentAssets + totalFixedAssets;

  const totalCurrentLiabilities = balanceData.currentLiabilities.creditors + 
                                  balanceData.currentLiabilities.loans + 
                                  balanceData.currentLiabilities.gstPayable;
  const totalLongTermLiabilities = balanceData.longTermLiabilities.loans;
  const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;

  const totalEquity = totalAssets - totalLiabilities;

  if (loading) return <div className="loading">Loading balance sheet...</div>;

  return (
    <div className="report-analysis-container">
      <div className="page-header">
        <h2>Balance Sheet</h2>
        <button 
          className="add-btn"
          onClick={fetchBalanceSheet}
        >
          🔄 Refresh
        </button>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              border: 'none',
              borderRight: '1px solid #ddd',
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none',
              background: 'white'
            }}
          >
            <option value={1}>January</option>
            <option value={2}>February</option>
            <option value={3}>March</option>
            <option value={4}>April</option>
            <option value={5}>May</option>
            <option value={6}>June</option>
            <option value={7}>July</option>
            <option value={8}>August</option>
            <option value={9}>September</option>
            <option value={10}>October</option>
            <option value={11}>November</option>
            <option value={12}>December</option>
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none',
              background: 'white'
            }}
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="balance-sheet-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
        <div className="balance-section" style={{ background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#2c5f2d', borderBottom: '2px solid #2c5f2d', paddingBottom: '8px' }}>Assets</h3>
          <div className="balance-items">
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '10px', marginBottom: '8px', color: '#555' }}>Current Assets</h4>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Cash & Bank Balance</span>
              <span className="amount-green" style={{ fontWeight: '500', color: '#28a745' }}>₹{balanceData.currentAssets.cash.toLocaleString()}</span>
            </div>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Accounts Receivable (Debtors)</span>
              <span className="amount-green" style={{ fontWeight: '500', color: '#28a745' }}>₹{balanceData.currentAssets.debtors.toLocaleString()}</span>
            </div>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Stock / Inventory</span>
              <span className="amount-green" style={{ fontWeight: '500', color: '#28a745' }}>₹{balanceData.currentAssets.stock.toLocaleString()}</span>
            </div>
            <div className="balance-item subtotal" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '13px', fontWeight: '600', borderTop: '1px solid #e0e0e0', marginTop: '5px', background: '#f0f8f0' }}>
              <span>Total Current Assets</span>
              <span className="amount-green" style={{ color: '#28a745' }}>₹{totalCurrentAssets.toLocaleString()}</span>
            </div>

            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '15px', marginBottom: '8px', color: '#555' }}>Fixed Assets</h4>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Fixed Assets</span>
              <span className="amount-green" style={{ fontWeight: '500', color: '#28a745' }}>₹{balanceData.fixedAssets.totalValue.toLocaleString()}</span>
            </div>
            <div className="balance-item subtotal" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '13px', fontWeight: '600', borderTop: '1px solid #e0e0e0', marginTop: '5px', background: '#f0f8f0' }}>
              <span>Total Fixed Assets</span>
              <span className="amount-green" style={{ color: '#28a745' }}>₹{totalFixedAssets.toLocaleString()}</span>
            </div>

            <div className="balance-item total" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px', fontWeight: 'bold', borderTop: '2px solid #555', marginTop: '10px', background: '#e8f5e9', border: '2px solid #555', borderRadius: '4px', paddingLeft: '10px', paddingRight: '10px' }}>
              <span>Total Assets</span>
              <span className="amount-green" style={{ color: '#2c5f2d' }}>₹{totalAssets.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="balance-section" style={{ background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#dc3545', borderBottom: '2px solid #dc3545', paddingBottom: '8px' }}>Liabilities & Equity</h3>
          <div className="balance-items">
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '10px', marginBottom: '8px', color: '#555' }}>Current Liabilities</h4>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Accounts Payable (Creditors)</span>
              <span className="amount-red" style={{ fontWeight: '500', color: '#dc3545' }}>₹{balanceData.currentLiabilities.creditors.toLocaleString()}</span>
            </div>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Short-term Loans</span>
              <span className="amount-red" style={{ fontWeight: '500', color: '#dc3545' }}>₹{balanceData.currentLiabilities.loans.toLocaleString()}</span>
            </div>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>GST Payable</span>
              <span className="amount-red" style={{ fontWeight: '500', color: '#dc3545' }}>₹{balanceData.currentLiabilities.gstPayable.toLocaleString()}</span>
            </div>
            <div className="balance-item subtotal" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '13px', fontWeight: '600', borderTop: '1px solid #e0e0e0', marginTop: '5px', background: '#fff5f5' }}>
              <span>Total Current Liabilities</span>
              <span className="amount-red" style={{ color: '#dc3545' }}>₹{totalCurrentLiabilities.toLocaleString()}</span>
            </div>

            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '15px', marginBottom: '8px', color: '#555' }}>Long-term Liabilities</h4>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Long-term Loans</span>
              <span className="amount-red" style={{ fontWeight: '500', color: '#dc3545' }}>₹{balanceData.longTermLiabilities.loans.toLocaleString()}</span>
            </div>
            <div className="balance-item subtotal" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '13px', fontWeight: '600', borderTop: '1px solid #e0e0e0', marginTop: '5px', background: '#fff5f5' }}>
              <span>Total Long-term Liabilities</span>
              <span className="amount-red" style={{ color: '#dc3545' }}>₹{totalLongTermLiabilities.toLocaleString()}</span>
            </div>

            <div className="balance-item subtotal" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '13px', fontWeight: '600', borderTop: '1px solid #e0e0e0', marginTop: '5px', background: '#fff5f5' }}>
              <span>Total Liabilities</span>
              <span className="amount-red" style={{ color: '#dc3545' }}>₹{totalLiabilities.toLocaleString()}</span>
            </div>

            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '15px', marginBottom: '8px', color: '#555' }}>Equity</h4>
            <div className="balance-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px' }}>
              <span>Owner's Equity</span>
              <span style={{ fontWeight: '500', color: '#007bff' }}>₹{totalEquity.toLocaleString()}</span>
            </div>

            <div className="balance-item total" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px', fontWeight: 'bold', borderTop: '2px solid #555', marginTop: '10px', background: '#ffe8e8', border: '2px solid #555', borderRadius: '4px', paddingLeft: '10px', paddingRight: '10px' }}>
              <span>Total Liabilities & Equity</span>
              <span style={{ color: '#dc3545' }}>₹{(totalLiabilities + totalEquity).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginTop: '20px' }}>
        <div style={{ background: 'linear-gradient(135deg, #2c5f2d 0%, #3a7d3e 100%)', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', color: 'white' }}>
          <h3 style={{ fontSize: '13px', marginBottom: '8px', opacity: '0.9' }}>Total Assets</h3>
          <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>₹{totalAssets.toLocaleString()}</p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #dc3545 0%, #e8515f 100%)', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', color: 'white' }}>
          <h3 style={{ fontSize: '13px', marginBottom: '8px', opacity: '0.9' }}>Total Liabilities</h3>
          <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>₹{totalLiabilities.toLocaleString()}</p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #007bff 0%, #1a8cff 100%)', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', color: 'white' }}>
          <h3 style={{ fontSize: '13px', marginBottom: '8px', opacity: '0.9' }}>Net Worth (Equity)</h3>
          <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>₹{totalEquity.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheet;
